import React from 'react';
import { Label, Icon } from '@nooz-optics/nooz-components-lib';
import { IButton, IColor, IIcon, IInput } from 'utils/types/contentful';
import clsx from 'clsx';
import { Button } from '../../../Elements';

export interface TextInputProps extends IInput {
  onChange: Function;
  button: Element;
  color: IColor;
  button: IButton;
  hasError: boolean;
  icon: IIcon;
}

const returnTextInputVariant = (variant: string, color: IColor): string => {
  const selectColor = (color && color.classname) || 'primary';
  const focus = `focus:ring-1 focus:ring-${selectColor} focus:border-${selectColor}`;

  switch (variant) {
    case 'contained':
      return `bg-white border text-gray-900 placeholder-gray-500 focus:outline-none ${focus} focus:placeholder-gray-400 `;
    case 'outlined':
      return ` border border-${selectColor} text-white placeholder-white focus:outline-none ${focus} focus:placeholder-gray-400 bg-transparent`;
    case 'white':
      return 'bg-white border border-black ';
    default:
      return `bg-white border text-gray-900 placeholder-gray-500 focus:outline-none ${focus} focus:placeholder-gray-400 `;
  }
};

const returnTextInputSize = (size: string): string => {
  switch (size) {
    case 'base':
      return 'pl-3 pr-10 py-2';
    case 'lg':
      return 'pl-3 pr-10 py-3';
    default:
      return 'pl-3 pr-10 py-2';
  }
};

export const TextInput: React.FC<TextInputProps> = ({
  button,
  color,
  hasError,
  icon,
  label,
  name,
  placeholder,
  register,
  required,
  size,
  type,
  variant,
}) => {
  const returnTextInputIcon = () => {
    if (hasError) {
      return (
        <div className='absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none'>
          <Icon
            iconKey='MdError'
            color='red-500'
          />
        </div>
      );
    } else if (icon) {
      return (
        <div className='absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none'>
          <Icon {...icon} />
        </div>
      );
    }
  };

  const returnTextInputLabel = () => {
    if (!label) {
      return;
    } else {
      const labelColor = variant === 'outlined' && { classname: 'white' };
      return (
        <Label
          {...label}
          color={labelColor}
          htmlFor={name}
          type='label'
        />
      );
    }
  };

  const returnTextInputButton = () =>
    button && (
      <div className='mt-4 lg:mt-0 lg:ml-4 h-full'>
        <Button
          {...button}
          type='contained'
          size='base'
        />
      </div>
    );

  return (
    <div className='w-full lg:items-center lg:flex'>
      <div className='w-full'>
        {returnTextInputLabel()}
        <div className='w-full mt-1 relative rounded-md shadow-sm'>
          <input
            type={type}
            name={name}
            id={name}
            required={required}
            className={clsx(
              returnTextInputSize(size),
              returnTextInputVariant(variant, color),
              'appearance-none min-w-0 w-full rounded-md py-2 px-4 text-base'
            )}
            placeholder={placeholder?.text}
            {...(register && register(name))}
          />
          {returnTextInputIcon()}
        </div>
      </div>
      {returnTextInputButton()}
    </div>
  );
};
